// lib/colour.ts

import type { CSSProperties } from "react";

const HEX_PATTERN = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i;
const RGB_PATTERN =
  /^rgb\s*\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\)$/i;

const DEFAULT_COLOUR = "#2563eb"; // Tailwind blue-600
const LIGHT_TEXT = "#ffffff";

type RgbTuple = [number, number, number];

function clampByte(value: number): number {
  return Math.max(0, Math.min(255, Math.round(value)));
}

function normaliseHex(value: string): string {
  const match = value.trim().match(HEX_PATTERN);
  if (!match) {
    throw new Error(`Unsupported hex colour: ${value}`);
  }
  let hex = match[1].toLowerCase();
  if (hex.length === 3) {
    hex = hex
      .split("")
      .map((char) => char + char)
      .join("");
  }
  return `#${hex}`;
}

function rgbTupleToHex([r, g, b]: RgbTuple): string {
  return `#${(
    (1 << 24) |
    (clampByte(r) << 16) |
    (clampByte(g) << 8) |
    clampByte(b)
  )
    .toString(16)
    .slice(1)}`;
}

function parseColour(input: string): RgbTuple {
  const trimmed = input.trim();
  const hexMatch = trimmed.match(HEX_PATTERN);
  if (hexMatch) {
    const hex = normaliseHex(trimmed).slice(1);
    const int = parseInt(hex, 16);
    return [(int >> 16) & 255, (int >> 8) & 255, int & 255];
  }

  const rgbMatch = trimmed.match(RGB_PATTERN);
  if (rgbMatch) {
    return [
      clampByte(Number.parseInt(rgbMatch[1]!, 10)),
      clampByte(Number.parseInt(rgbMatch[2]!, 10)),
      clampByte(Number.parseInt(rgbMatch[3]!, 10)),
    ];
  }

  throw new Error(`Unsupported colour format: ${input}`);
}

export function hexToRgb(hex: string): RgbTuple {
  try {
    return parseColour(hex);
  } catch {
    return parseColour(DEFAULT_COLOUR);
  }
}

function rgbToHex(value: RgbTuple): string {
  return rgbTupleToHex(value).toLowerCase();
}

/** Relative luminance (WCAG 2.x). */
function luminance([r, g, b]: RgbTuple): number {
  const transform = (channel: number) => {
    const v = channel / 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  };

  const [R, G, B] = [r, g, b].map(transform) as RgbTuple;
  return 0.2126 * R + 0.7152 * G + 0.0722 * B;
}

function contrastRatio(bg: string, fg: string): number {
  const backgroundL = luminance(hexToRgb(bg));
  const foregroundL = luminance(hexToRgb(fg));
  const lighter = Math.max(backgroundL, foregroundL);
  const darker = Math.min(backgroundL, foregroundL);
  return (lighter + 0.05) / (darker + 0.05);
}

function adjustTowards(colour: string, target: string, amount: number): string {
  const [r, g, b] = hexToRgb(colour);
  const [tr, tg, tb] = hexToRgb(target);
  const mixed: RgbTuple = [
    r + (tr - r) * amount,
    g + (tg - g) * amount,
    b + (tb - b) * amount,
  ];
  return rgbToHex(mixed);
}

function ensureAccessibleContrast(
  baseColour: string | null | undefined,
  minRatio = 4.5
): { bg: string; txt: string; border: string } {
  const base = rgbToHex(hexToRgb(baseColour ?? DEFAULT_COLOUR));

  let adjustedBg = base;
  let ratio = contrastRatio(adjustedBg, LIGHT_TEXT);

  for (let i = 0; i < 10 && ratio < minRatio; i++) {
    adjustedBg = adjustTowards(adjustedBg, "#000000", 0.12);
    ratio = contrastRatio(adjustedBg, LIGHT_TEXT);
  }

  if (ratio < minRatio) {
    adjustedBg = "#1f2937";
    ratio = contrastRatio(adjustedBg, LIGHT_TEXT);
    if (ratio < minRatio) {
      adjustedBg = "#111827";
    }
  }

  return { bg: adjustedBg, txt: LIGHT_TEXT, border: base };
}

export function pillStyle(colour: string | null | undefined): CSSProperties {
  const { bg, txt, border } = ensureAccessibleContrast(colour);
  return {
    backgroundColor: bg,
    color: txt,
    borderColor: border,
  };
}
